import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase, faSearch } from '@fortawesome/free-solid-svg-icons';
import { faTwitter, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { useAuthStore } from '@/store/auth';
import { UserDropdownMenu } from '@/components/common/UserDropdownMenu';
import Logo from '@/components/common/Logo';

interface PublicLayoutProps {
  className?: string;
}

export const PublicLayout: React.FC<PublicLayoutProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  const [scrolled, setScrolled] = useState(false);
  const [activeScene, setActiveScene] = useState(0);

  // 在首页时显示走马灯切换按钮
  const isHomePage = location.pathname === '/';

  // 监听滚动，添加阴影效果
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 处理搜索提交
  const handleSearch = (query: string) => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-dark text-slate-100 flex flex-col ${className}`}>
      {/* 导航栏 - 玻璃拟态深色主题 */}
      <header className={`glass-nav sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'shadow-xl shadow-black/20' : ''
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-3">
          {/* Logo */}
          <Logo />

            {/* 中间占位 */}
            <div className="flex-1"></div>

            {/* 右侧区域: 搜索框 + 导航链接 + 用户操作 */}
            <div className="flex items-center space-x-4">
              {/* 搜索框 */}
              <div className="hidden md:block relative" style={{ width: '260px' }}>
                <input
                  type="text"
                  placeholder="搜索数据集..."
                  className="input-glass w-full pr-10"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const value = (e.target as HTMLInputElement).value.trim();
                      if (value) {
                        navigate(`/search?q=${encodeURIComponent(value)}`);
                      }
                    }
                  }}
                  style={{ fontSize: '14px', fontFamily: 'inherit', height: '40px' }}
                />
                <button 
                  onClick={(e) => {
                    const input = (e.currentTarget.previousElementSibling as HTMLInputElement);
                    const value = input.value.trim();
                    if (value) {
                      navigate(`/search?q=${encodeURIComponent(value)}`);
                    }
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-primary-400 transition-colors"
                >
                  <FontAwesomeIcon icon={faSearch} className="w-4 h-4" />
                </button>
              </div>

              {/* 导航链接 */}
              <nav className="hidden lg:flex items-center space-x-4">
                <Link to="/intro" className="text-sm font-medium text-slate-300 hover:text-primary-400 transition-colors">
                  平台介绍
                </Link>
                <Link to="/help" className="text-sm font-medium text-slate-300 hover:text-primary-400 transition-colors">
                  帮助中心
                </Link>
                <Link to="/my-data" className="text-sm font-medium text-slate-300 hover:text-primary-400 transition-colors">
                  我的数据中心
                </Link>
              </nav>

              {/* 用户操作按钮 */}
              {isAuthenticated ? (
                <UserDropdownMenu variant="minimal" />
              ) : (
                <>
                  <Link to="/auth/login" className="text-sm font-medium text-slate-300 hover:text-slate-50 transition-colors">登录</Link>
                  <Link 
                    to="/auth/register" 
                    className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg shadow-primary-500/20 hover:shadow-primary-500/40 transition-all"
                  >
                    免费开始
                  </Link>
                </>
              )}
            </div>
          </div>
          
          {/* 主导航 - 走马灯切换按钮（仅在首页显示） */}
          {isHomePage && (
            <nav className="flex items-center space-x-8 py-3 border-t border-white/5">
              <button
                onClick={() => setActiveScene(0)}
                className={`relative text-sm font-medium transition-colors pb-3 ${
                  activeScene === 0 ? 'text-primary-400' : 'text-slate-300 hover:text-slate-50'
                }`}
              >
                智能平台
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 transform origin-left transition-transform duration-300 ${
                  activeScene === 0 ? 'scale-x-100' : 'scale-x-0'
                }`}></span>
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 transform origin-left transition-transform duration-300 scale-x-0 hover:scale-x-100"></span>
              </button>
              <button
                onClick={() => setActiveScene(1)}
                className={`relative text-sm font-medium transition-colors pb-3 ${
                  activeScene === 1 ? 'text-primary-400' : 'text-slate-300 hover:text-slate-50'
                }`}
              >
                数据集市
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 transform origin-left transition-transform duration-300 ${
                  activeScene === 1 ? 'scale-x-100' : 'scale-x-0'
                }`}></span>
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 transform origin-left transition-transform duration-300 scale-x-0 hover:scale-x-100"></span>
              </button>
              <button
                onClick={() => setActiveScene(2)}
                className={`relative text-sm font-medium transition-colors pb-3 ${
                  activeScene === 2 ? 'text-primary-400' : 'text-slate-300 hover:text-slate-50'
                }`}
              >
                解决方案
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 transform origin-left transition-transform duration-300 ${
                  activeScene === 2 ? 'scale-x-100' : 'scale-x-0'
                }`}></span>
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 transform origin-left transition-transform duration-300 scale-x-0 hover:scale-x-100"></span>
              </button>
            </nav>
          )}
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="flex-1"> 
        {isHomePage ? (
          <Outlet context={{ activeScene, setActiveScene }} />
        ) : (
          <Outlet />
        )}
      </main>

      {/* 页脚 - 玻璃拟态风格 */}
      <footer className="relative bg-dark-800/80 backdrop-blur-xl border-t border-white/5 py-12 mt-auto">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* 公开信息 */}
            <div>
              <h3 className="text-slate-200 font-bold mb-4 text-sm">数据南岸</h3>
              <p className="text-xs text-slate-400">
                数据管理平台，提供全方位的数据解决方案。
              </p>
            </div>

            {/* 产品 */}
            <div>
              <h3 className="text-slate-200 font-bold mb-4 text-sm">产品</h3>
              <ul className="space-y-2 text-xs">
                <li><Link to="/search" className="text-slate-400 hover:text-primary-400 transition-colors">数据集市</Link></li>
                <li><Link to="/intro" className="text-slate-400 hover:text-primary-400 transition-colors">解决方案</Link></li>
                <li><Link to="/auth/register" className="text-slate-400 hover:text-primary-400 transition-colors">开始试用</Link></li>
              </ul>
            </div>

            {/* 资源 */}
            <div>
              <h3 className="text-slate-200 font-bold mb-4 text-sm">资源</h3>
              <ul className="space-y-2 text-xs">
                <li><Link to="/help" className="text-slate-400 hover:text-primary-400 transition-colors">文档</Link></li>
                <li><Link to="/help" className="text-slate-400 hover:text-primary-400 transition-colors">教程</Link></li>
                <li><Link to="/filter-demo" className="text-slate-400 hover:text-primary-400 transition-colors">功能演示</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-primary-400 transition-colors">支持</Link></li>
              </ul>
            </div>

            {/* 公司 */}
            <div>
              <h3 className="text-slate-200 font-bold mb-4 text-sm">公司</h3>
              <ul className="space-y-2 text-xs">
                <li><Link to="/about" className="text-slate-400 hover:text-primary-400 transition-colors">关于我们</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-primary-400 transition-colors">联系我们</Link></li>
                <li><Link to="/privacy" className="text-slate-400 hover:text-primary-400 transition-colors">隐私政策</Link></li>
              </ul>
            </div>
          </div>

          {/* 底部信息 */}
          <div className="pt-8 border-t border-white/5 mt-8 text-center space-y-2">
            <div className="text-xs text-slate-500">
              &copy; 2025 数据南岸 Inc. 保留所有权利。
            </div>
            <div className="text-xs text-slate-500 hover:text-slate-400 transition-colors">
              <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
                苏ICP备2023030414号-5
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;